/**
 * Regelversion 11: Spielbare Opposition.
 *
 * Wer eine Bundestagswahl verliert oder durch ein Misstrauensvotum gestürzt wird, kann wählen:
 * in die Opposition gehen und weiterspielen oder mit einer neuen Partei neu anfangen.
 *
 *  - Die Computerparteien bilden eine Regierung (stärkste Partei plus passende Partner bis zur Mehrheit).
 *    Sie beschließt Gesetze, gerät in Krisen und Affären und verliert mit der Zeit Zustimmung.
 *  - Die eigene Partei hat jeden Monat Aktionspunkte: Anträge, Aktuelle Stunden, Talkshows,
 *    Landeswahlkämpfe, Normenkontrollklagen, Untersuchungsausschüsse und das konstruktive Misstrauensvotum.
 *  - Am Ende der Wahlperiode zieht die Partei als Herausforderin in den Wahlkampf. Wird sie stärkste Kraft,
 *    führt sie die Koalitionsverhandlungen und regiert wieder. Sonst bleibt sie in der Opposition oder gründet neu.
 *  - Jahre in der Opposition zählen nicht als Regierungszeit (Punkte, Orden), stehen aber in der Karriere.
 *  Nur im Einzelspieler.
 */
import { S, log } from './state';
import { since } from './version';
import { rnd, shuffle } from './rng';
import { clamp, dateYear, de } from './util';
import { PARTIES, OUTLETS } from '../content/parties';
import { NEED, MONTHS } from '../content/consts';
import { STATES, ISS } from '../content/states';
import { EVENTS } from '../content/events';
import { satWith, posOf, PA, PN } from './rules';
import { issuesNow, axes } from './axes';
import { isDyn, dynRelevant, dynTarget, dynStep } from './dyn8';
import { LTW } from './race';
import { landtag11, partyTick, afterElection11 } from './parties11';
import { zgTick, zgMax } from './zeitgeist';
import { press } from './media';
import { pub } from './opinion';
import { electionResult, isWon } from './election';
import { turnout9 } from './elect9';
import { themeOptions } from './campaign';
import { funding } from './ads';
import { buildDuel } from './duel';
import { toast } from './govern';
import { AXN } from '../content/axes';
import { demS, demRestore, sturzElection, aiDemStep, aiDemUndo, STEP } from './dem11';
import { govLaw12 } from './rivals12';

export type OppLaw = { t: string; iss: string; m: number; vr: number; sued?: number; out?: 'ok' | 'kassiert' };
export type OppState = {
  gov: string[]; chan: string; appr: number; ap: number; bonus: number;
  until: number; start: number; s0: number; how: 'election' | 'misstrauen';
  laws: OppLaw[]; scandal: { t: string; m: number } | null; used: Record<string, number>;
  stats: { antr: number; ok: number; klag: number; win: number; ua: number; ltw: number; talk: number };
  hist: { m: number; y: number; a: number }[]; early?: boolean; renewed?: boolean; mTried?: number; last?: string | null;
};
export type OppLogEntry = { a: number; b: number; s0: number; s1: number | null; how: string; back: boolean; via?: string; gov: string[] };

export const oppOn = (): boolean => since(11) && !S.mp;
export const O = (): OppState => (S as any).opp;
const A = () => S as any;

/** Aktionspunkte: pro Monat einer, höchstens vier. */
export const AP_MAX11 = 4;
export const COST = { antrag: 2, stunde: 1, talk: 1, land: 1, klage: 1, ua: 2, misstrauen: 3, demo: 2, renew: 4 } as const;
/** Version 11: Makel nach einem Sturz (Anzahl Wahlen, bis er verblasst). */
export const makel = (): number => A().makel || 0;
/** Für Normenkontrollklage und Untersuchungsausschuss braucht es ein Viertel des Bundestags. */
/** Stellschrauben (mit Bot-Partien abgestimmt). */
export const K11 = { pollAppr: .12, tired: .15, tiredP: .07, camp: .4, decay: .94, fatigue: .14, stunde: [25, .3, 1.4], bmax: 6, keep: .75 };
export const QUORUM = Math.ceil(630 / 4);

/** Umfrage der eigenen Partei in der Opposition. */
export function oppPoll(): number {
  const o = O(); if (!o) return S.elec;
  return clamp(S.elec + o.bonus + (50 - o.appr) * K11.pollAppr + Math.min(0, S.basis || 0) - makel() * 2.5, 3, 55);
}

/** Regierung der Computerparteien: stärkste Partei plus Partner mit der besten Programm-Nähe bis zur Mehrheit. */
export function formGov(res: any): { gov: string[]; chan: string } {
  const ids: string[] = (res.inP || []).filter((k: string) => k !== 'YOU' && PARTIES[k] && !PARTIES[k].gone).sort((a: string, b: string) => res.seats[b] - res.seats[a]);
  let best: { gov: string[]; sc: number } | null = null, fallback: string[] | null = null;
  for (const chan of ids.filter((k) => k !== 'HO').slice(0, 2)) {
    const cand = ids.filter((k) => k !== chan && k !== 'HO');
    const combos: string[][] = [[]]; cand.forEach((a, i) => { combos.push([a]); cand.slice(i + 1).forEach((b, j) => { combos.push([a, b]); cand.slice(i + j + 2).forEach((c) => combos.push([a, b, c])); }); });
    for (const ps of combos) {
      const seats = [chan, ...ps].reduce((s, k) => s + (res.seats[k] || 0), 0);
      const fit = ps.length ? ps.reduce((s, p) => s + satWith(p, posOf(chan)), 0) / ps.length : 1;
      const sc = fit - ps.length * .06 + (chan === ids[0] ? .15 : 0);
      if (seats >= NEED && (!best || sc > best.sc)) best = { gov: [chan, ...ps], sc };
      if (!fallback && ps.length === 1) fallback = [chan, ...ps];
    }
  }
  const gov = best?.gov || fallback || [ids.find((k) => k !== 'HO') || ids[0]];
  return { gov, chan: gov[0] };
}
export const govSeats = (): number => O().gov.reduce((s, k) => s + (S.res.seats[k] || 0), 0);

/** Oppositionsparteien, die mit uns stimmen würden (für Klage und Ausschuss), nach Nähe sortiert. */
export function allies(min = .5): string[] {
  if (makel() > 0) return []; // Version 11: Nach einem Sturz will niemand mit euch zusammenarbeiten
  const g = new Set(O().gov);
  return (S.res.inP || []).filter((k: string) => k !== 'YOU' && !g.has(k) && k !== 'HO' && S.res.seats[k] && satWith(k, S.you) >= min)
    .sort((a: string, b: string) => satWith(b, S.you) - satWith(a, S.you));
}
/** Reicht es mit Partnern für ein Viertel der Sitze? Liefert die Mitstreiter oder null. */
export function quorum(): string[] | null {
  let seats = S.res.seats.YOU || 0; const w: string[] = [];
  if (seats >= QUORUM) return w;
  for (const k of allies(.4)) { seats += S.res.seats[k]; w.push(k); if (seats >= QUORUM) return w; }
  return null;
}
/** Wer würde ein konstruktives Misstrauensvotum mittragen? */
export function mBloc(): { ids: string[]; seats: number } {
  const o = O(), ids = ['YOU', ...allies(.55)];
  if (o.appr < 30) for (const p of o.gov.slice(1)) if (satWith(p, S.you) >= satWith(p, posOf(o.chan)) - .05) ids.push(p);
  return { ids, seats: ids.reduce((s, k) => s + (S.res.seats[k] || 0), 0) };
}
export const mReady = (): boolean => O().appr < 36 && (O().mTried == null || S.m - O().mTried! >= 12);

// ── Wechsel in die Opposition ──

export function oppStart() {
  if (!oppOn() || S.phase !== 'end') return;
  const sturz = S.end === 'sturz', misst = S.end === 'misstrauen', lost = S.end === 'election' && !S.won;
  if (!misst && !lost && !sturz) return;
  if (sturz || demS()?.fall) { A().makel = 2; demRestore(); if (demS()) demS()!.fall = null; } // Version 11: Makel nach dem Sturz, Institutionen wiederhergestellt
  if (sturz) { sturzElection(); log('Eine Übergangsregierung übernimmt. Bei der Neuwahl bricht eure Partei ein.', 'bad'); }
  let gov: string[], chan: string;
  if (misst) {
    const bloc = (S.endBy || []).filter((k) => k !== 'YOU' && S.res.seats[k]).sort((a, b) => S.res.seats[b] - S.res.seats[a]); gov = [];
    for (const k of bloc) { gov.push(k); if (gov.reduce((s, x) => s + S.res.seats[x], 0) >= NEED) break; }
    if (!gov.length) gov = formGov(S.res).gov; chan = gov[0];
  }
  else { A().resPrev = S.res; S.res = S.res2!; S.res2 = null; S.elec = S.final; S.m += 2; ({ gov, chan } = formGov(S.res)); }
  const until = misst ? Math.max(S.m + 12, S.m0 + MONTHS) : S.m + MONTHS;
  S.leg++; A().oppLegs = (A().oppLegs || 0) + 1;
  const keep = ['kraft', 'done', 'repealed'];
  S.laws.filter((L) => !keep.includes(L.stage)).forEach((L) => { if (L.iss && S.issues[L.iss]) S.issues[L.iss].law = null; });
  S.laws = S.laws.filter((L) => keep.includes(L.stage));
  S.promises = (S.promises || []).filter((p: any) => p.leg < S.leg);
  Object.assign(S, { partners: [], agree: null, ress: {}, end: null, early: false, won: false, editing: null, crisis: null, dec: null, scandal: null, pickFor: null, ptag: null, oppBill: null, budget: null, bview: false, event: null, minority: false, basis: 0, press: 0, review: null, linger: null });
  A().endBy = undefined; A().chal = false; A().oppMove = null; A().donOffer = null; A().ua = null; A().fiscal = null; A().lapsed = null;
  S.m0 = S.m; S.legYear = dateYear(S.m);
  if (!S.ltwQ.length) S.ltwQ = shuffle(Object.keys(STATES));
  const appr = misst ? 50 : 55;
  A().opp = { gov, chan, appr, ap: 2, bonus: 0, until, start: S.m, s0: S.elec, how: misst ? 'misstrauen' : 'election', laws: [], scandal: null, used: {},
    stats: { antr: 0, ok: 0, klag: 0, win: 0, ua: 0, ltw: 0, talk: 0 }, hist: [{ m: S.m, y: S.elec, a: appr }], last: null } as OppState;
  S.phase = 'opp' as any; S.tab = 'lage';
  log(`${PN(chan)} stellt die neue Regierung${gov.length > 1 ? ` mit ${gov.slice(1).map(PN).join(' und ')}` : ''}. ${S.name} geht in die Opposition.`, 'bad');
  press(OUTLETS[0].n, `Neue Regierung im Amt: ${gov.map(PA).join(', ')}`, 0);
}

// ── Ein Monat in der Opposition ──

const SCANDALS = [
  'Ein Staatssekretär soll Aufträge an Bekannte vergeben haben',
  'Ein Minister hat bei seiner Doktorarbeit abgeschrieben',
  'Berater-Millionen im Verteidigungsministerium',
  'Gelöschte Chats aus dem Kanzleramt',
  'Eine Ministerin soll Lobbyisten Gesetzestexte schreiben lassen',
  'Spendenaffäre um die Kanzlerpartei',
];

function govLaw() {
  const o = O(), recent = new Set(o.laws.filter((L) => S.m - L.m < 18).map((L) => L.iss));
  const cand = issuesNow().filter((i: any) => S.issues[i.id]?.on && !recent.has(i.id)).sort((a: any, b: any) => S.issues[b.id].sev - S.issues[a.id].sev).slice(0, 3);
  if (!cand.length) return;
  const I = cand[Math.floor(rnd() * cand.length)], ls = (ISS[I.id]?.laws || []).filter((T: any) => !T.custom && !T.req);
  if (since(12)) { const r = govLaw12(o, I, ls); if (!r) return; o.laws.push({ t: r.t, iss: I.id, m: S.m, vr: r.vr }); log(`Die Regierung beschließt „${r.t}“${r.d ? `: ${r.d}` : ''}.`); return; } // Version 12: echte Vorlagen
  const T = ls[Math.floor(rnd() * ls.length)]; if (!T) return;
  const st = S.issues[I.id]; st.sev = clamp(st.sev - (10 + rnd() * 8), 0, 100); st.cut = Math.max(st.cut || 0, .25);
  o.laws.push({ t: T.t, iss: I.id, m: S.m, vr: Math.round((.08 + rnd() * .34) * 100) / 100 });
  o.appr = clamp(o.appr + 1, 10, 80);
  log(`Die Regierung beschließt „${T.t}“.`);
}

function govEvent(): boolean {
  const o = O();
  if (rnd() < .08) {
    const ev = EVENTS[Math.floor(rnd() * EVENTS.length)];
    if (ev.iss && !Object.keys(ev.iss).every((k) => S.issues[k]?.on)) return false;
    if (ev.coal && o.gov.length < 2) return false;
    for (const k in (ev.iss || {})) S.issues[k].sev = clamp(S.issues[k].sev + ev.iss[k], 0, 100);
    if (ev.gr) S.gr += ev.gr;
    const good = (ev.b || 0) > 0 || (ev.gr || 0) > 0;
    o.appr = clamp(o.appr + (good ? 1.5 : ev.crisis ? -3 : ev.coal ? -2.5 : -1.2), 10, 80);
    log(`${ev.t}: ${ev.s}`, good ? '' : 'bad'); press(OUTLETS[ev.coal ? 3 : 0].n, `${ev.t}: ${ev.s}`, 0);
    return true;
  }
  if (!o.scandal && rnd() < .025) {
    const t = SCANDALS[Math.floor(rnd() * SCANDALS.length)];
    o.scandal = { t, m: S.m }; o.appr = clamp(o.appr - 2, 10, 80);
    log(`Affäre: ${t}. Die Opposition kann einen Untersuchungsausschuss einsetzen.`, 'bad'); press(OUTLETS[3].n, `${t}`, 0);
    return true;
  }
  return false;
}

function ltw11() {
  if (!S.ltwQ.length) S.ltwQ = shuffle(Object.keys(STATES));
  const id = S.ltwQ.shift()!, st = STATES[id], old = S.gov[id], o = O(); S.ltwNext = S.m + 3;
  if (!S.ltwQ.length) S.ltwQ = shuffle(Object.keys(STATES)).filter((x) => x !== id); // nächste Wahl schon bekannt (Landeswahlkampf)
  const share = clamp(oppPoll() + ((S.stOff || {})[id] || 0) * .8 + (rnd() * 4 - 2), 3, 60);
  const win = share >= (old === 'YOU' ? LTW.hold : LTW.take);
  S.gov[id] = win ? 'YOU' : (old === 'YOU' ? (o.gov.includes(st.alt) ? st.alt : o.chan) : old);
  landtag11(id, share);
  if (win) { o.stats.ltw++; o.bonus = clamp(o.bonus + (old === 'YOU' ? .4 : 1), -K11.bmax, K11.bmax); } else if (old === 'YOU') o.bonus = clamp(o.bonus - .6, -K11.bmax, K11.bmax);
  const txt = `Landtagswahl in ${st.n}: ${S.name} holt ${de(share)} %. ` + (win ? (old === 'YOU' ? 'Ihr regiert weiter im Land.' : 'Ihr übernehmt die Landesregierung.') : (old === 'YOU' ? `Die Landesregierung geht an ${PA(S.gov[id])}.` : `${PA(S.gov[id])} regiert weiter.`));
  log(txt, win ? 'good' : old === 'YOU' ? 'bad' : ''); toast(txt, win ? 'good' : old === 'YOU' ? 'bad' : '');
}

function month() {
  const o = O(); S.m++;
  issuesNow().forEach((i: any) => {
    const st = S.issues[i.id]; if (!st) return; st.prev = st.sev;
    if (!st.on && i.at != null && st.solved == null && S.m >= i.at && dynRelevant(i.id)) { st.on = true; if (isDyn(i.id)) st.sev = Math.max(30, dynTarget(i.id)); log(`Neues Problem auf der Agenda: ${i.n}.`, 'bad'); }
    if (st.on) st.sev = isDyn(i.id) ? dynStep(i.id, st, rnd() * .6 - .3, 0) : clamp(st.sev + i.rate * (1 - (st.cut || 0)) + (rnd() * .6 - .3), 0, 100);
  });
  if (rnd() < .4) govLaw();
  { const X = aiDemStep(); if (X) { o.laws.push({ t: X.n, iss: 'demokratie', m: S.m, vr: .55, dem: X.id } as any); o.appr = clamp(o.appr - 3, 10, 80); log(`Die Regierung greift die Demokratie an: „${X.n}“. ${X.d}`, 'bad'); press(OUTLETS[0].n, `Kritik aus allen Lagern: Regierung beschließt „${X.n}“`, 0); toast(`Regierung: „${X.n}“`, 'bad'); } }
  // Die Regierung kümmert sich auch ohne Gesetz um die dringendsten Probleme
  issuesNow().forEach((i: any) => { const st = S.issues[i.id]; if (st?.on && st.sev > 70) st.sev = clamp(st.sev - .5, 0, 100); });
  govEvent();
  // Klagen entscheiden
  for (const L of o.laws) if (L.sued != null && !L.out && S.m - L.sued >= 4) {
    if (rnd() < L.vr) { L.out = 'kassiert'; o.stats.win++; if ((L as any).dem) aiDemUndo((L as any).dem); o.appr = clamp(o.appr - 4, 10, 80); o.bonus = clamp(o.bonus + 2, -K11.bmax, K11.bmax); if (S.issues[L.iss]) S.issues[L.iss].sev = clamp(S.issues[L.iss].sev + 5, 0, 100); log(`Karlsruhe gibt eurer Klage recht: „${L.t}“ ist verfassungswidrig.`, 'good'); toast(`Klage erfolgreich: „${L.t}“ gekippt`, 'good'); press(OUTLETS[0].n, `Schlappe für die Regierung: Karlsruhe kippt „${L.t}“`, 0); }
    else { L.out = 'ok'; o.bonus = clamp(o.bonus - .8, -K11.bmax, K11.bmax); log(`Karlsruhe weist eure Klage gegen „${L.t}“ ab.`, 'bad'); }
  }
  if (o.scandal && S.m - o.scandal.m > 8) o.scandal = null;
  // Wirtschaft und Zustimmung
  const on = issuesNow().filter((i: any) => S.issues[i.id]?.on), avg = on.length ? on.reduce((s: number, i: any) => s + S.issues[i.id].sev, 0) / on.length : 45;
  S.gr += (1.3 - (avg - 45) * .02 - S.gr) * .12 + (rnd() - .5) * .1; S.al = clamp(S.al + ((6.2 - (S.gr - .6) * .9) - S.al) * .12, 2, 15); S.debt += -S.saldo / 12;
  const target = 52 - (S.m - o.start) * K11.fatigue - (avg - 45) * .35 + (S.gr - .6) * 3 - (S.al - 6.2) * 2;
  o.appr = clamp(o.appr + (target - o.appr) * .06 + (rnd() * 2 - 1), 10, 80);
  o.bonus *= K11.decay; o.ap = Math.min(AP_MAX11, o.ap + 1);
  for (const id in S.stOff) S.stOff[id] *= .98;
  if (S.m >= S.ltwNext) ltw11();
  partyTick(); zgTick();
  if (!o.early && o.gov.length > 1 && o.appr < 33 && o.until - S.m > 8 && rnd() < .03) {
    o.early = true; o.until = S.m + 3;
    log(`Die Koalition aus ${o.gov.map(PA).join(', ')} zerbricht. Der Bundestag wird aufgelöst, in drei Monaten wird neu gewählt.`, 'good'); toast('Die Regierung ist zerbrochen: Neuwahlen', 'good');
  }
  o.hist.push({ m: S.m, y: Math.round(oppPoll() * 10) / 10, a: Math.round(o.appr * 10) / 10 });
  if (o.hist.length > 80) o.hist.shift();
  if (S.m >= o.until) startChallenger();
}

/** `n` Monate weiter (stoppt beim Wahlkampf). */
export function oppRun(n: number) {
  if (S.phase !== ('opp' as any) || !O()) return;
  S.toast = null; O().last = null;
  for (let i = 0; i < Math.max(1, Math.min(6, n)); i++) { month(); if (S.phase !== ('opp' as any)) break; }
}

// ── Aktionen ──

const pay = (k: keyof typeof COST): boolean => { const o = O(); if (!o || S.phase !== ('opp' as any) || o.ap < COST[k]) return false; o.ap -= COST[k]; return true; };
/** Wiederholung desselben Themas binnen sechs Monaten wirkt nur halb. */
const fresh = (key: string): number => { const o = O(), u = o.used[key]; o.used[key] = S.m; return u != null && S.m - u < 6 ? .5 : 1; };
const say = (t: string, k = '') => { O().last = t; log(t, k); };

export function oxAntrag(iss: string) {
  const st = S.issues[iss]; if (!st?.on || st.sev < 30 || !pay('antrag')) return;
  const o = O(), f = fresh('a:' + iss), fit = satWith(o.chan, S.you), n = ISS[iss]?.n || iss; o.stats.antr++;
  if (st.sev >= 50 && rnd() < .2 + fit * .35) {
    st.sev = clamp(st.sev - 8, 0, 100); o.bonus = clamp(o.bonus + .8 * f, -K11.bmax, K11.bmax); o.stats.ok++;
    say(`Euer Antrag zu ${n} findet eine Mehrheit. Die Regierung muss nachlegen, ihr bekommt die Anerkennung.`, 'good');
  } else {
    o.bonus = clamp(o.bonus + clamp((st.sev - 40) / 40, .1, .5) * f, -K11.bmax, K11.bmax); o.appr = clamp(o.appr - .4 * f, 10, 80);
    say(`Euer Antrag zu ${n} wird von der Regierungsmehrheit abgelehnt. Die Debatte bringt euch trotzdem Aufmerksamkeit.`);
  }
}
export function oxStunde(iss: string) {
  const st = S.issues[iss]; if (!st?.on || !pay('stunde')) return;
  const o = O(), f = fresh('s:' + iss), d = clamp((st.sev - 35) / K11.stunde[0], K11.stunde[1], K11.stunde[2]) * f;
  o.appr = clamp(o.appr - d, 10, 80); o.bonus = clamp(o.bonus + .2 * f, -K11.bmax, K11.bmax);
  say(`Aktuelle Stunde zu ${ISS[iss]?.n || iss}: Ihr stellt die Regierung zur Rede. Ihre Zustimmung sinkt um ${de(d)} Punkte.`, d >= 1 ? 'good' : '');
}
export function oxTalk(ax: string) {
  if (!axes().includes(ax) || !pay('talk')) return;
  const o = O(), f = fresh('t:' + ax), y = S.you[ax] ?? 0, p = pub(ax), fit = 1 - Math.abs(y - p) / 4;
  o.bonus = clamp(o.bonus + (.1 + fit * .3) * f, -K11.bmax, K11.bmax); o.stats.talk++;
  if (y !== p) { const z = (S.zg ??= {}); z[ax] = clamp((z[ax] || 0) + Math.sign(y - p) * .06 * f, -zgMax(), zgMax()); }
  say(`Talkshow zu ${AXN[ax] || ax}: ${fit >= .7 ? 'Eure Haltung trifft die Stimmung im Land.' : 'Ihr werbt für eine Haltung, die viele noch nicht teilen. Die Stimmung bewegt sich ein wenig.'}`);
}
export function oxLand(id: string) {
  if (!STATES[id] || !pay('land')) return;
  S.stOff[id] = clamp((S.stOff[id] || 0) + 2, -K11.bmax, K11.bmax);
  say(`Ihr macht Wahlkampf in ${STATES[id].n}.`);
}
export function oxKlage(i: number) {
  const o = O(), L = o?.laws[i]; if (!L || L.sued != null || !quorum() || !pay('klage')) return;
  L.sued = S.m; o.stats.klag++;
  const w = quorum()!;
  say(`Ihr reicht${w.length ? ` mit ${w.map(PA).join(' und ')}` : ''} Normenkontrollklage gegen „${L.t}“ in Karlsruhe ein. Das Urteil kommt in etwa vier Monaten.`);
}
export function oxUA() {
  const o = O(); if (!o?.scandal || !quorum() || !pay('ua')) return;
  const t = o.scandal.t; o.scandal = null; o.stats.ua++;
  o.appr = clamp(o.appr - 4, 10, 80); o.bonus = clamp(o.bonus + 1.5, -K11.bmax, K11.bmax);
  if (rnd() < .4) { o.appr = clamp(o.appr - 2, 10, 80); say(`Untersuchungsausschuss: ${t}. Die Zeugenaussagen sind verheerend, ein Regierungsmitglied tritt zurück.`, 'good'); }
  else say(`Untersuchungsausschuss: ${t}. Die Regierung gerät unter Druck.`, 'good');
}
/** Version 11: Großdemonstration für die Demokratie (nur wenn die Regierung sie angreift). */
export function oxDemo() {
  const D = demS(); if (!D || D.v >= 80 || !pay('demo')) return;
  const o = O(), f = fresh('demo'); o.appr = clamp(o.appr - 3 * f, 10, 80); o.bonus = clamp(o.bonus + 1 * f, -K11.bmax, K11.bmax);
  say(`Ihr ruft mit Verbänden und Kirchen zur Großdemonstration für die Demokratie auf. Hunderttausende kommen.`, 'good');
}
/** Version 11: Erneuerung nach einem Sturz: neue Spitze, klare Distanzierung. */
export function oxRenew() {
  if (makel() <= 0 || O()?.renewed || !pay('renew')) return;
  const o = O(); o.renewed = true; A().makel = makel() - 1; o.bonus = clamp(o.bonus - 1, -K11.bmax, K11.bmax);
  say('Erneuerung: Die Partei wählt eine neue Spitze und distanziert sich klar vom Kurs der gestürzten Regierung. Das kostet erst Stimmen, öffnet aber Türen.', 'good');
}
export function oxMisstrauen() {
  const o = O(); if (!o || !mReady() || !pay('misstrauen')) return;
  const b = mBloc();
  if (b.seats >= NEED) { comeBack('misstrauen', b.ids.filter((k) => k !== 'YOU')); return; }
  o.mTried = S.m; o.bonus = clamp(o.bonus - 1.5, -K11.bmax, K11.bmax); o.appr = clamp(o.appr + 3, 10, 80);
  say(`Euer Misstrauensvotum scheitert mit ${b.seats} von ${NEED} nötigen Stimmen. Die Regierung geht gestärkt daraus hervor.`, 'bad');
}

// ── Rückkehr an die Macht ──

function pushLog(s1: number | null, back: boolean, via?: string) {
  const o = O(), L: OppLogEntry[] = (A().oppLog ??= []);
  L.push({ a: dateYear(o.start), b: dateYear(S.m), s0: o.s0, s1, how: o.how, back, via, gov: o.gov.slice() });
}
function comeBack(via: 'misstrauen' | 'wahl', partners: string[] = []) {
  if (via === 'misstrauen') {
    pushLog(Math.round(oppPoll() * 10) / 10, true, 'misstrauen');
    S.elec = S.res.share.YOU; S.elYear = dateYear(S.m);
    log(`Konstruktives Misstrauensvotum: ${['YOU', ...partners].map(PA).join(', ')} stürzen die Regierung. ${S.name} stellt die Kanzlerin.`, 'good');
    press(OUTLETS[0].n, `Machtwechsel ohne Wahl: ${S.name} übernimmt das Kanzleramt`, 0);
  }
  if (demS()) demRestore(); // Version 11: Die neue Regierung stellt die Institutionen wieder her
  S.leg++; A().legOff = S.leg; A().backVia = via; A().opp = null; A().chal = false;
  S.partners = []; S.agree = null; S.fin = 'YOU'; S.ress = {}; S.end = null; S.early = false; S.phase = 'night';
}
/** Nach gewonnener Wahl als Herausforderin: Koalitionsverhandlungen. */
export function oppReturn() {
  if (S.phase !== 'end' || !A().chal || !S.won || !S.res2) return;
  A().resPrev = S.res; S.res = S.res2; S.res2 = null; S.elec = S.final; S.m += 2;
  comeBack('wahl');
}

// ── Wahlkampf als Herausforderin ──

export function startChallenger() {
  const o = O(); A().chal = true;
  const c: any = { first: false, chal: true, step: 'themen', themes: [], stops: {}, opp: o.chan, ans: [], promise: [], opts: themeOptions(false) };
  c.fund = funding({ share: oppPoll(), pos: S.you, first: false, arbg: S.lob?.arbg, soz: S.lob?.soz, gew: S.lob?.gew, lastShare: S.elec }); c.ads = {};
  S.camp = c; c.q = buildDuel({ first: true, gov: false, opp: PA(o.chan), coalition: false });
  S.early = !!o.early; S.phase = 'campaign'; S.editing = null; S.bview = false;
  log(o.early ? 'Der Wahlkampf für die Neuwahl beginnt. Ihr tretet als Herausforderer an.' : 'Der Wahlkampf beginnt. Ihr tretet als Herausforderer an.');
}

/** Wahlergebnis als Herausforderin. Aufruf aus finishCampaign. */
export function chalFinish(c: any, b: any, tot: number) {
  const o = O(), T9 = turnout9(b.th + b.tour + b.duel + b.ads, false, c);
  const x = clamp(oppPoll() + tot * K11.camp + (rnd() * 3 - 1.5) + T9.bonus, 3, 55);
  const tired = clamp(52 - o.appr, -6, 30), adj: Record<string, number> = {};
  // Die Parteien behalten einen Teil ihres letzten Ergebnisses, die Regierung verliert mit sinkender Zustimmung
  for (const k in PARTIES) if (!PARTIES[k].gone && S.res.share[k] != null) adj[k] = (S.res.share[k] - PARTIES[k].base) * K11.keep;
  o.gov.forEach((k, i) => { adj[k] = (adj[k] || 0) - tired * (i ? K11.tiredP : K11.tired) - ((demS()?.v ?? 85) < 70 ? 3 : 0); }); // Version 11: Wer die Demokratie angreift, verliert
  const r: any = electionResult(x, undefined, 0, adj); r.turnout = T9;
  afterElection11(r);
  S.final = x; S.res2 = r; S.won = isWon(r); S.elYear = dateYear(S.m); S.end = 'election'; S.phase = 'end';
  pushLog(Math.round(x * 10) / 10, !!S.won, S.won ? 'wahl' : undefined);
  if (makel() > 0) A().makel = makel() - 1; // Version 11: Der Makel verblasst mit jeder Wahl
}

